import { parse } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllClasses } from '~entities/classes/api/get-all-classes';
import { useGetMobileInventorAppContent } from '~entities/mobile-inventor-push/api/get-app-content';
import { useDashboardHandoff } from '~entities/mobile-inventor-push/api/dashboard-handoff';
import { useSendPush } from '~entities/mobile-inventor-push/api/send-push';
import type { PushAudienceInput } from '~entities/mobile-inventor-push/model/types';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useGetTags } from '~entities/tags/api/get-groups';

import { convertLocalToUTC } from '~shared/utils/recital-timezone';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Textarea } from '~shared/shadcn/ui/textarea';

type AudienceType = PushAudienceInput['type'];

interface PushComposeFormProps {
  appName?: string;
}

const initialFormState = {
  audienceType: 'all' as AudienceType,
  tagId: '',
  classId: '',
  notificationText: '',
  title: '',
  detail: '',
  imageUrl: '',
  sendAt: '',
  actionType: 'none' as 'none' | 'appContent' | 'url',
  actionText: '',
  actionTarget: '',
};

function validateAudience(audience: PushAudienceInput): string | null {
  if (audience.type === 'tag' && !audience.tagId?.trim()) {
    return 'Select a tag for your audience.';
  }
  if (audience.type === 'class' && !audience.classId?.trim()) {
    return 'Select a class for your audience.';
  }
  return null;
}

function validateActionButton(
  actionType: 'none' | 'appContent' | 'url',
  actionText: string,
  actionTarget: string,
): string | null {
  if (actionType === 'none') return null;
  if (!actionText.trim()) return 'Enter a label for the action button.';
  if (!actionTarget.trim()) {
    return actionType === 'url'
      ? 'Enter a URL for the action button.'
      : 'Select app content for the action button.';
  }
  if (actionType === 'url') {
    try {
      const parsed = new URL(actionTarget.trim());
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'Enter a valid http(s) URL for the action button.';
      }
    } catch {
      return 'Enter a valid URL for the action button.';
    }
  }
  return null;
}

/** Only allow navigation to Mobile Inventor HTTPS handoff URLs. */
function isSafeMobileInventorRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const host = parsed.hostname.toLowerCase();
    return (
      host === 'mobileinventor.com' || host.endsWith('.mobileinventor.com')
    );
  } catch {
    return false;
  }
}

export const PushComposeForm = ({ appName }: PushComposeFormProps) => {
  const { data: appContent } = useGetMobileInventorAppContent(true);
  // Dropdown needs the full tag list; default API page size would truncate it.
  const { data: tags } = useGetTags({ page: 1, limit: 1000 });
  const { data: classesData } = useGetAllClasses();
  const sendPush = useSendPush();
  const dashboardHandoff = useDashboardHandoff();
  const studioTimezone =
    useCurrentStudio()?.timezone?.trim() || 'America/New_York';

  const [form, setForm] = useState(initialFormState);
  const [confirmBroadcastOpen, setConfirmBroadcastOpen] = useState(false);
  // Stable across retries of the *same* compose payload so a mid-batch
  // failure can resume. Regenerated when the user edits the form.
  const sendIdempotencyKeyRef = useRef<string | null>(null);
  const sendPayloadFingerprintRef = useRef<string | null>(null);

  const audience = useMemo((): PushAudienceInput => {
    if (form.audienceType === 'tag') {
      return { type: 'tag', tagId: form.tagId };
    }
    if (form.audienceType === 'class') {
      return { type: 'class', classId: form.classId };
    }
    return { type: form.audienceType };
  }, [form.audienceType, form.tagId, form.classId]);

  const buildMessage = () => ({
    notificationText: form.notificationText.trim(),
    title: form.title.trim() || undefined,
    detail: form.detail.trim() || undefined,
    imageUrl: form.imageUrl.trim() || undefined,
    actionButton:
      form.actionType === 'none'
        ? undefined
        : {
            text: form.actionText.trim(),
            type: form.actionType,
            target: form.actionTarget.trim(),
          },
  });

  const resetForm = () => {
    setForm(initialFormState);
    sendIdempotencyKeyRef.current = null;
    sendPayloadFingerprintRef.current = null;
  };

  const parseScheduleUtc = (): Date | null => {
    if (!form.sendAt) return null;
    // convertLocalToUTC falls back to the current time on a failed conversion,
    // so validate the raw datetime-local value first — otherwise a malformed
    // input silently becomes "now" and trips the future check instead of the
    // validity check.
    const localDate = parse(form.sendAt, "yyyy-MM-dd'T'HH:mm", new Date(0));
    if (Number.isNaN(localDate.getTime())) {
      return null;
    }
    const scheduledAt = convertLocalToUTC(form.sendAt, studioTimezone);
    if (Number.isNaN(scheduledAt.getTime())) {
      return null;
    }
    return scheduledAt;
  };

  const validateForm = (): boolean => {
    if (!form.notificationText.trim()) {
      toast.error('Notification text is required.');
      return false;
    }

    const audienceError = validateAudience(audience);
    if (audienceError) {
      toast.error(audienceError);
      return false;
    }

    const actionError = validateActionButton(
      form.actionType,
      form.actionText,
      form.actionTarget,
    );
    if (actionError) {
      toast.error(actionError);
      return false;
    }

    if (form.sendAt) {
      const scheduledAt = parseScheduleUtc();
      if (!scheduledAt) {
        toast.error('Enter a valid schedule date and time.');
        return false;
      }
      if (scheduledAt.getTime() <= Date.now()) {
        toast.error('Schedule time must be in the future.');
        return false;
      }
    }

    return true;
  };

  const isComposeBusy = sendPush.isPending || dashboardHandoff.isPending;

  const submitPush = async () => {
    // Block while either path is in flight so a send and MI handoff cannot
    // race for the same audience.
    if (isComposeBusy) return;
    if (!validateForm()) return;

    const scheduledAt = form.sendAt ? parseScheduleUtc() : null;
    if (form.sendAt && !scheduledAt) {
      toast.error('Enter a valid schedule date and time.');
      return;
    }

    const message = buildMessage();
    const sendAt = scheduledAt ? scheduledAt.toISOString() : undefined;
    // Reuse the key only when audience/message/schedule are unchanged so a
    // failed send can resume; mint a new key after the user edits the form.
    const fingerprint = JSON.stringify({ audience, message, sendAt });
    if (
      !sendIdempotencyKeyRef.current ||
      sendPayloadFingerprintRef.current !== fingerprint
    ) {
      sendIdempotencyKeyRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sendPayloadFingerprintRef.current = fingerprint;
    }

    try {
      await sendPush.mutateAsync({
        audience,
        message,
        sendAt,
        idempotencyKey: sendIdempotencyKeyRef.current,
      });
      setConfirmBroadcastOpen(false);
      resetForm();
    } catch {
      // Keep key + fingerprint so an identical retry resumes the failed claim.
      // Error toast handled in mutation hook.
    }
  };

  const handleSendClick = () => {
    if (isComposeBusy) return;
    if (!validateForm()) return;
    if (audience.type === 'all') {
      setConfirmBroadcastOpen(true);
      return;
    }
    void submitPush();
  };

  const handleDashboardHandoff = async () => {
    if (isComposeBusy) return;

    const audienceError = validateAudience(audience);
    if (audienceError) {
      toast.error(audienceError);
      return;
    }

    // Open synchronously under the user gesture so the browser does not block
    // the tab after the async handoff request completes.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      toast.error(
        'Popup blocked. Allow popups for this site, then try again.',
      );
      return;
    }

    let data: Awaited<ReturnType<typeof dashboardHandoff.mutateAsync>>;
    try {
      data = await dashboardHandoff.mutateAsync(audience);
    } catch {
      if (!popup.closed) popup.close();
      // Error toast handled in mutation hook.
      return;
    }

    // The server handoff has already completed at this point, so any failure
    // below is about opening the tab — never claim the handoff was cancelled.
    if (!isSafeMobileInventorRedirect(data.redirect)) {
      if (!popup.closed) popup.close();
      toast.error(
        'Mobile Inventor returned an unexpected redirect URL, so the composer was not opened. The handoff is still recorded in your send history.',
      );
      return;
    }

    if (popup.closed) {
      toast.error(
        'The Mobile Inventor tab was closed before the composer could open. Check your send history or try again.',
      );
      return;
    }

    try {
      // Sever window.opener before navigating to the allowlisted MI host to
      // prevent reverse tabnabbing (the child can no longer reach back in).
      popup.opener = null;
      popup.location.href = data.redirect;
      toast.success('Opened Mobile Inventor composer');
    } catch {
      if (!popup.closed) popup.close();
      toast.error(
        'Could not open the Mobile Inventor composer. Check your send history or try again.',
      );
    }
  };

  const isScheduled = Boolean(form.sendAt);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Compose</CardTitle>
          <CardDescription>
            Send a push notification to parents with your studio&apos;s mobile
            app installed.
            {appName ? ` Connected app: ${appName}.` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='space-y-2'>
            <Label>Audience</Label>
            <Select
              value={form.audienceType}
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  audienceType: value as AudienceType,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All parents</SelectItem>
                <SelectItem value='tag'>By tag</SelectItem>
                <SelectItem value='class'>By class</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.audienceType === 'tag' ? (
            <div className='space-y-2'>
              <Label>Tag</Label>
              <Select
                value={form.tagId || undefined}
                onValueChange={(value) =>
                  setForm((current) => ({ ...current, tagId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select tag' />
                </SelectTrigger>
                <SelectContent>
                  {(tags?.items ?? []).map((tag) => (
                    <SelectItem key={tag._id} value={tag._id}>
                      {tag.fieldName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {form.audienceType === 'class' ? (
            <div className='space-y-2'>
              <Label>Class</Label>
              <Select
                value={form.classId || undefined}
                onValueChange={(value) =>
                  setForm((current) => ({ ...current, classId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select class' />
                </SelectTrigger>
                <SelectContent>
                  {(classesData ?? []).map((cls) => (
                    <SelectItem key={cls._id} value={cls._id}>
                      {cls.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className='space-y-2'>
            <Label htmlFor='notificationText'>
              Notification text <span className='text-destructive'>*</span>
            </Label>
            <Textarea
              id='notificationText'
              maxLength={255}
              value={form.notificationText}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  notificationText: event.target.value,
                }))
              }
              placeholder='Short message shown on the lock screen'
            />
            <p className='text-xs text-muted-foreground'>
              {form.notificationText.length}/255
              {form.imageUrl
                ? ' · Android may truncate around 50 chars with an image'
                : ''}
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='title'>Title (optional)</Label>
            <Input
              id='title'
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='detail'>Detail (optional, HTML)</Label>
            <Textarea
              id='detail'
              value={form.detail}
              onChange={(event) =>
                setForm((current) => ({ ...current, detail: event.target.value }))
              }
              rows={4}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='imageUrl'>Image URL (optional)</Label>
            <Input
              id='imageUrl'
              value={form.imageUrl}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  imageUrl: event.target.value,
                }))
              }
              placeholder='https://…'
            />
          </div>

          <div className='space-y-2'>
            <Label>Action button (optional)</Label>
            <Select
              value={form.actionType}
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  actionType: value as 'none' | 'appContent' | 'url',
                  // Clear mismatched targets when switching button type.
                  actionTarget: '',
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>None</SelectItem>
                <SelectItem value='appContent'>App content</SelectItem>
                <SelectItem value='url'>Website URL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.actionType !== 'none' ? (
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label>Button label</Label>
                <Input
                  value={form.actionText}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      actionText: event.target.value,
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>{form.actionType === 'url' ? 'URL' : 'App content'}</Label>
                {form.actionType === 'appContent' ? (
                  <Select
                    value={form.actionTarget || undefined}
                    onValueChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        actionTarget: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select content' />
                    </SelectTrigger>
                    <SelectContent>
                      {(appContent?.content ?? []).map((item) => (
                        <SelectItem key={item.contentId} value={item.contentId}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.actionTarget}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        actionTarget: event.target.value,
                      }))
                    }
                    placeholder='https://…'
                  />
                )}
              </div>
            </div>
          ) : null}

          <div className='space-y-2'>
            <Label htmlFor='sendAt'>Schedule (optional)</Label>
            <Input
              id='sendAt'
              type='datetime-local'
              value={form.sendAt}
              onChange={(event) =>
                setForm((current) => ({ ...current, sendAt: event.target.value }))
              }
            />
            <p className='text-xs text-muted-foreground'>
              Interpreted in your studio timezone ({studioTimezone}).
            </p>
          </div>

          <div className='flex flex-wrap gap-2 pt-2'>
            <Button
              disabled={isComposeBusy || !form.notificationText.trim()}
              onClick={handleSendClick}
            >
              {sendPush.isPending
                ? isScheduled
                  ? 'Scheduling…'
                  : 'Sending…'
                : isScheduled
                  ? 'Schedule push'
                  : 'Send now'}
            </Button>
            <Button
              variant='outline'
              disabled={isComposeBusy}
              onClick={() => void handleDashboardHandoff()}
            >
              {dashboardHandoff.isPending
                ? 'Opening…'
                : 'Open in MI composer'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={confirmBroadcastOpen}
        onOpenChange={(open) => {
          if (isComposeBusy) return;
          setConfirmBroadcastOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isScheduled
                ? 'Schedule for all parents?'
                : 'Send to all parents?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isScheduled
                ? 'This push will be scheduled for every parent in your studio. Double-check your message and schedule time before continuing.'
                : 'This push will be sent to every parent in your studio. Double-check your message before continuing.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isComposeBusy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isComposeBusy}
              onClick={(event) => {
                event.preventDefault();
                void submitPush();
              }}
            >
              {sendPush.isPending
                ? isScheduled
                  ? 'Scheduling…'
                  : 'Sending…'
                : isScheduled
                  ? 'Schedule push'
                  : 'Send push'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
